import type React from 'react';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import classNames from 'classnames';
import Translation from '../../../general/Translation/Translation';
import { useWithSiteUrl } from '../../../general/WebshopContext/WebshopContext';
import { useTypedSelector } from '../../Store/connectors';
import NotificationBox from '../../../../stories/Molecules/NotificationBox/NotificationBox';
import Icon from '../../../../stories/Atoms/Icon/Icon';
import styles from './ContinueToCheckout.module.scss';
import Button from '../../../../stories/Atoms/Button/Button';
import { window } from '../../../../js/globals';

const ContinueToCheckout: React.FC = () => {
  const withSiteUrl = useWithSiteUrl();
  const isAllowedToProceedToCheckout = useTypedSelector(
    ({ ui }) => ui.isAllowedToProceedToCheckout,
  );

  const cartContainsAlcohol = useTypedSelector(
    ({ cartItems }) => cartItems.cartContainsAlcohol,
  );
  const cartContainsStrongAlcohol = useTypedSelector(
    ({ cartItems }) => cartItems.cartContainsStrongAlcohol,
  );
  const [hasApprovedAlcohol, setHasApprovedAlcohol] = useState<boolean>(
    !cartContainsAlcohol,
  );
  const hasNotApprovedAlcoholCheck = cartContainsAlcohol && !hasApprovedAlcohol;
  const [hasExpandedAlcoholLicenseInfo, setHasExpandedAlcoholLicenseInfo] =
    useState<boolean>(false);

  const updateAlcoholApproval = (event: ChangeEvent<HTMLInputElement>) => {
    setHasApprovedAlcohol(event.target.checked);
  };

  const redirectToCheckout = () => {
    if (window) {
      window.location.href = withSiteUrl('/checkout');
    }
  };

  return (
    <>
      {!isAllowedToProceedToCheckout && (
        <NotificationBox
          type="info"
          className="mt-4"
        >
          <Translation
            pageString="checkout"
            stringId="cannot_combine_strong_alcohol_explanation"
          />
        </NotificationBox>
      )}

      {isAllowedToProceedToCheckout && cartContainsAlcohol && (
        <NotificationBox
          hasDefaultIcon={false}
          type="warning"
          className="mt-4"
        >
          <div className={styles.AgeCheck}>
            <input
              autoComplete="off"
              className={classNames(styles.AgeCheckInput, 'form-checkbox')}
              id="ageCheck"
              type="checkbox"
              checked={hasApprovedAlcohol}
              onChange={updateAlcoholApproval}
            />
            <label
              htmlFor="ageCheck"
              className="pointer"
            >
              {cartContainsStrongAlcohol ? (
                <Translation
                  pageString="checkout"
                  stringId="strong_alcohol_check_message"
                />
              ) : (
                <Translation
                  pageString="checkout"
                  stringId="alcohol_check_message"
                />
              )}
            </label>
            <div
              aria-label="collapse alcohol license info"
              className={styles.AgeCheckIcon}
              role="button"
              tabIndex={0}
              onClick={() =>
                setHasExpandedAlcoholLicenseInfo(!hasExpandedAlcoholLicenseInfo)
              }
            >
              <Icon name="fa-circle-info" />
            </div>
          </div>
        </NotificationBox>
      )}

      {cartContainsAlcohol && hasExpandedAlcoholLicenseInfo && (
        <NotificationBox
          hasDefaultIcon={false}
          type="info"
          className="mt-4"
        >
          <Translation
            pageString="checkout"
            stringId="alcohol_license_explanation"
          />
        </NotificationBox>
      )}

      <Button
        primary
        fluid
        size="large"
        disabled={!isAllowedToProceedToCheckout || hasNotApprovedAlcoholCheck}
        onClick={redirectToCheckout}
        className="mt-8"
      >
        <Translation
          pageString="checkout"
          stringId="continue-to-order"
        />
      </Button>
    </>
  );
};

export default ContinueToCheckout;
